// src/components/TapArea.jsx
//
// The core TapTap mechanic: two independent targets, left and right. Each one is
// tapped on its own, disappears, then reappears after `reappearSpeed` (handled
// in App.js). In normal mode the targets are coloured orbs; in kids mode they
// are bouncing animals. Colour applies to the orb itself, never the background.
import React from 'react';
import { COLOR_OPTIONS, ANIMAL_OPTIONS } from '../constants';
import {
  getFocusOrbVisual,
  getSleepDuskOrbVisual,
  getSunriseOrbVisual,
} from '../orbVisuals';
import useIsTablet from '../hooks/useIsTablet';

// orbSize is a fixed physical length (cm), tuned to feel right in the hand on
// a phone. Left unscaled, it looks sparse/lost on an iPad's much larger
// physical screen, so tablets get a flat multiplier on top of it.
const TABLET_ORB_SCALE = 1.8;

// orbSpacing is 0-100; map it to how far each target sits from centre (as a % of
// width). Capped so even the largest orbs stay fully on screen.
const MAX_OFFSET = 38; // percent from centre at spacing = 100
const MAX_HALF_PCT = 42; // hard on-screen cap for each orb centre's offset, before
                         // subtracting its own radius below so the orb's edge —
                         // not just its centre — never reaches the screen edge.
const EDGE_MARGIN_CM = 0.3; // small buffer past the orb's own edge, in absolute cm
                            // so it holds regardless of orientation/viewport width.
// The orbs must never visually overlap, but the minimum here should reflect the
// visible orb only (plus a small gap) — not the button's padded hit area, which
// was previously inflating the minimum far past what "nearly touching" needs.
const MIN_GAP_CM = 0.1;
// Every orb runs a continuous breathing pulse (see index.css @keyframes
// orbPulse) that grows it to 106% of its rest size every 2s, in kids-mode-off
// rendering. The no-overlap minimum must hold at that peak, not just at rest —
// otherwise the two orbs visibly touch for an instant on every pulse cycle.
const PULSE_PEAK_SCALE = 1.06;
// Kids Mode draws each animal emoji at 1.2x the orb size, and emoji glyphs are
// wider than their font size, so the no-overlap spacing must use this visual
// width rather than the orb's.
const KIDS_VISUAL_SCALE = 1.2;

const TapArea = ({
  kidsMode,
  orb1Animal = 'cat',
  orb2Animal = 'cat',
  orb1Visible,
  orb2Visible,
  orb1Color = 'blue',
  orb2Color = 'blue',
  orbSize = 3,
  orbSpacing = 40,
  onOrbTap,
  disabled = false, // e.g. while the settings panel is open
  demoActive = false, // first-launch tour running: lift orbs in landscape so the
                      // bottom coach card can't cover them (portrait has room).
  fadeDurationMs = 0, // structured routines supply their own visual pace
  routineProgress = 0, // drives time-based visuals such as the Wake Up sunrise
}) => {
  // How far each orb sits from centre. The spacing slider gives a % of width, but
  // we clamp it to a fixed minimum distance in cm (half the orb + a small gap) so
  // the orbs can never overlap, and to a max that subtracts the orb's own radius
  // (in cm) from the % cap so its edge — not just its centre — stays on screen.
  // CSS resolves the mixed units (cm vs %) at layout time.
  const isTablet = useIsTablet();
  const effectiveOrbSize = isTablet ? orbSize * TABLET_ORB_SCALE : orbSize;
  const spacingPct = (Math.min(Math.max(orbSpacing, 0), 100) / 100) * MAX_OFFSET;
  // A fixed cm font size can make two animals wider than a portrait phone,
  // especially when Size is set high. Cap the emoji by the viewport and use
  // that same rendered size (including the button padding) for positioning.
  const animalFontSize = `min(${effectiveOrbSize * KIDS_VISUAL_SCALE}cm, 23vw, 31vh)`;
  const animalWidth = `calc(min(${effectiveOrbSize * KIDS_VISUAL_SCALE * 1.2}cm, 27.6vw, 37.2vh) + 40px)`;
  const visualRadius = kidsMode
    ? `calc(${animalWidth} / 2)`
    : `${effectiveOrbSize * PULSE_PEAK_SCALE / 2}cm`;
  const minHalf = `calc(${visualRadius} + ${MIN_GAP_CM}cm)`;
  const maxHalf = `calc(${kidsMode ? 50 : MAX_HALF_PCT}% - ${visualRadius} - ${EDGE_MARGIN_CM}cm)`;
  // At the largest orb size + widest spacing on a narrow screen, the no-overlap
  // minimum and the on-screen maximum can conflict — there's no distance that
  // satisfies both. The no-overlap minimum wins that conflict deliberately: a
  // few pixels of edge overflow on a decorative glow is far less broken than
  // the two tap targets visually merging into one.
  const offset = `clamp(${minHalf}, ${spacingPct}%, ${maxHalf})`;

  const renderTarget = (index) => {
    const orbNumber = index + 1;
    const visible = orbNumber === 1 ? orb1Visible : orb2Visible;
    const color = orbNumber === 1 ? orb1Color : orb2Color;
    const side = orbNumber === 1 ? 'left' : 'right';

    let inner;
    if (kidsMode) {
      const animalKey = orbNumber === 1 ? orb1Animal : orb2Animal;
      const animal = ANIMAL_OPTIONS[animalKey] || ANIMAL_OPTIONS.cat;
      inner = (
        <div
          className="text-center select-none leading-none"
          style={{
            fontSize: animalFontSize,
            filter: `drop-shadow(0 0 30px ${animal.color}66)`,
          }}
        >
          {animal.emoji}
        </div>
      );
    } else {
      const routineVisual = color === 'sunrise'
        ? getSunriseOrbVisual(routineProgress, orbNumber)
        : color === 'sleepDusk'
          ? getSleepDuskOrbVisual(routineProgress, orbNumber)
          : color === 'focus'
            ? getFocusOrbVisual(orbNumber)
            : null;
      const colorData = routineVisual || COLOR_OPTIONS[color] || COLOR_OPTIONS.blue;
      // Match the clean, luminous orbs used on the sign-in screen. Keeping this
      // as one simple circle also gives the tap target a clearer visual edge.
      const dim = `${effectiveOrbSize}cm`;
      const backgroundStyle = colorData.gradient
        ? { background: colorData.gradient }
        : {
            backgroundColor: routineVisual?.backgroundColor,
            backgroundImage: routineVisual?.backgroundImage,
          };
      inner = (
        <div
          data-testid={`orb-visual-${side}`}
          className="orb-pulse rounded-full"
          style={{
            width: dim,
            height: dim,
            // Sleep deliberately uses the same complete radial-gradient
            // construction as the standard TapTap orbs. Sunrise/Focus retain
            // a transitionable base colour with a translucent overlay.
            ...backgroundStyle,
            boxShadow: `0 0 38px 9px ${colorData.shadow}`,
            transition: routineVisual
              ? 'background-color 1100ms linear, box-shadow 1100ms linear'
              : undefined,
          }}
        />
      );
    }

    return (
      <button
        key={side}
        type="button"
        disabled={disabled || !visible}
        onClick={() => onOrbTap(orbNumber)}
        aria-label={`Tap ${side} ${kidsMode ? 'animal' : 'orb'}`}
        className={`absolute -translate-x-1/2 -translate-y-1/2 top-1/2 p-5 cursor-pointer bg-transparent border-0 ${
          demoActive ? 'landscape:top-[38%]' : ''
        } ${kidsMode ? 'transition-opacity duration-300' : ''}`}
        style={{
          left: index === 0 ? `calc(50% - ${offset})` : `calc(50% + ${offset})`,
          opacity: visible ? 1 : 0,
          transition: `opacity ${fadeDurationMs || (kidsMode ? 300 : 0)}ms ease-in-out`,
          pointerEvents: visible && !disabled ? 'auto' : 'none',
        }}
      >
        {inner}
      </button>
    );
  };

  return (
    <div className="flex-1 relative bg-black overflow-hidden">
      {renderTarget(0)}
      {renderTarget(1)}
    </div>
  );
};

export default TapArea;
